import "./App.css";
import Roles from "./components/Roles";
import { Route, Routes, useLocation } from "react-router-dom";
import RolePage from "./components/Roles/Role";
import NavBar from "./components/NavBar";
import Footer from "./components/Footer";
import CreateRole from "./components/Roles/CreateRole/CreateRole";
import { useEffect, useState } from "react";
import useApiToken from "./hooks/useApiToken";
import { Spinner } from "./components/Shared/Spinner";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";



import Candidates from "./components/Candidates";
import Dashboard from "./components/Dashboard";
import CandidatePage from "./components/Candidates/Candidate";
import AllAssignmentsDetails from "./components/AllAssignmentsDetails";
import Assessments from "./components/Assessment/Assessments";
import QuestionsComponent from "./components/Question/Questions";
import AddEditQuestion from "./components/Question/AddEditQuestion";
import AddEditAssessment from "./components/Assessment/AddEditAssessment";
import AddQuestionGroup from "./components/Question/AddQuestionGroup";
import AssessmentQuestions from "./components/Assessment/AssessmentQuestions";
import RankingBoard from "./components/Ranking/RankingBoard";
import Quarantine from "./components/Quarantine/Quarantine";
import Workflows from "./components/Workflows/Workflows";
import JobDescriptions from "./components/JobDescriptions/JobDescriptions";
import AssessmentTemplates from "./components/Template/AssessmentTemplates";
import AddEditAssessmentTemplates from "./components/Template/AddEditAssessmentTemplates";
import LocationBoard from "./components/LocationStats/LocationBoard";
import ActionsLog from "./components/LocationStats/ActionsLog/ActionsLog";
import TemplateLogs from "./components/Template/TemplateLogsComponent";
import InterviewCalendar from "./components/Roles/Calendar";
import ValidateCV from "./components/Roles/Modals/ValidateCV";
import Resultspage from "./components/Candidates/CandidateAssessmentResults";
import Tests from "./components/Test/Tests";
import SlotCandidates from "./components/Test/SlotCandidates";
import Issues from "./components/Issues/Issues";
import PromptConfigPage from "./components/PromptConfigBuilder/PromptConfigPage";
// import { hasRecruitmentAccess } from "./helpers/auth";
import Unauthorized from "./components/Unauthorized";
import { jwtDecode } from "jwt-decode";
import { has } from "@amcharts/amcharts4/.internal/core/utils/Array";
import { set } from "date-fns";
import { AdminDashboard } from "./components/OfferedCandidates/AdminDashboard";
import { SubmitCredentialsPage } from "./components/OfferedCandidates/SubmitCredentialsPage";
import { useGlobalWindowResetAlerts } from "./hooks/useGlobalWindowResetAlerts";
type JwtPayload = {
  roles?: string[];
  exp?: number;
};

function App() {
  const { instance, inProgress } = useMsal();
  const token = useApiToken();
  const [themeIsDark, setThemeIsDark] = useState<boolean>(false);
  const [showFooter, setShowFooter] = useState<boolean>(true);
  const [hasPermission, setHasPermission] = useState<boolean>(false);
  const location = useLocation();

  const isAuthenticated = useIsAuthenticated();
  
  useGlobalWindowResetAlerts();

  useEffect(() => {
    const defaultThemeIsDark = Boolean(localStorage.getItem("darkTheme"));
    if (defaultThemeIsDark) setThemeIsDark(true);
  }, []);

  useEffect(() => {
    if (themeIsDark) {
      localStorage.setItem("darkTheme", "true");
      document.body.classList.add("dark");
    } else {
      localStorage.removeItem("darkTheme");
      document.body.classList.remove("dark");
    }
  }, [themeIsDark]);

  useEffect(() => {
    async function handleLogin() {
      try {
        const resToken = await instance.handleRedirectPromise();
        if (resToken !== null) {
          instance.setActiveAccount(resToken.account);
        }
        if (!isAuthenticated && inProgress === InteractionStatus.None) {
          await instance.loginRedirect();
        }

      } catch (er) {
        console.log(er);
      }
    }
    handleLogin();

    const hasRecruitmentAccess = async (): Promise<boolean> => {
      const account = instance.getActiveAccount();
      if (!account) return false;

      try {
        const accessTokenRequest = {
          scopes: [
            "api://7da5a83f-58c1-4fdf-b5e4-bb00829c8ac1/Recruitment.Read",
          ],
          account: account,
        };

        const response = await instance.acquireTokenSilent(accessTokenRequest);

        try {
          const decoded = jwtDecode<JwtPayload>(response.accessToken);

          if (decoded.roles) {
            return decoded.roles.includes("app.access");
          }

          return false;
        } catch (err) {
          console.error("Failed to decode token:", err);
          return false;
        }
      } catch (error) {
        console.error("Error acquiring API token:", error);
        return false;
      }
    };

    (async () => {
      const access = await hasRecruitmentAccess();
      if (import.meta.env.VITE_ENVIRONMENT !== "PROD") {
        setHasPermission(true);
      } else {
        if (access) {
          setHasPermission(true);
        }
      }
    })();

    
  }, [isAuthenticated, inProgress, instance]);

  useEffect(() => {
    if (
      location.pathname.match(
        /\/roles\/[a-zA-Z0-9-]{8}-[a-zA-Z0-9-]{4}-[a-zA-Z0-9-]{4}-[a-zA-Z0-9-]{4}-[a-zA-Z0-9-]{12}(?:\/(?:edit|candidates)\/?)?$/,
      ) ||
      location.pathname.startsWith("/rankboard") ||
      location.pathname.startsWith("/quarantine") ||
      location.pathname.startsWith("/templates") ||
      location.pathname.startsWith("/questions") ||
      location.pathname.startsWith("/assessments") ||
      location.pathname.startsWith("/issues")
    ) {
      setShowFooter(false);
    } else {
      setShowFooter(true);
    }
  }, [location.pathname]);


  if (!isAuthenticated) {
    return <div className="bg-white h-screen"></div>;
  }

  if (isAuthenticated && !token && inProgress === InteractionStatus.None) {
    return (
      <div className="bg-white h-screen flex items-center justify-center">
        <Spinner height="h-10" width="w-10" />
      </div>
    );
  }

  if (isAuthenticated && !hasPermission) {
    return <Unauthorized />;
  }
  return (
    <>
      <NavBar setThemeIsDark={setThemeIsDark} themeIsDark={themeIsDark} />
      <Routes>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route
          path="/"
          element={
            <Roles/>
          }
        />
        <Route
          path="/roles"
          element={
            <Roles/>
          }
        />
        <Route path="/roles/:roleId" element={<RolePage />} />
        <Route path="/roles/:roleId/edit" element={<RolePage />} />
        <Route path="/roles/:roleId/candidates" element={<RolePage />} />
        <Route
          path="/roles/:roleId/candidates/:candidateId"
          element={<CandidatePage />}
        />
        <Route path="/roles/create" element={<CreateRole />} />
        <Route path="/roles/:roleId/create" element={<CreateRole />} />
        <Route path="/candidates" element={<Candidates />} />
        <Route path="/candidates/:candidateId" element={<CandidatePage />} />
        <Route path="/roles/calendar" element={<InterviewCalendar />} />
        <Route
          path="/roles/:roleId/assignments"
          element={<AllAssignmentsDetails />}
        />
        {/* Assessment Routes */}
        <Route path="/assessments" element={<Assessments />} />
        <Route path="/questions" element={<QuestionsComponent />} />
        <Route path="/questions/add" element={<AddEditQuestion />} />
        <Route path="/assessments/add" element={<AddEditAssessment />} />
        <Route path="/questions/group/add" element={<AddQuestionGroup />} />
        <Route path="/questions/edit/:id" element={<AddEditQuestion />} />
        <Route path="/assessments/edit/:id" element={<AddEditAssessment />} />
        <Route
          path="/assessment/questions/:id"
          element={<AssessmentQuestions />}
        />

        {/* Ranking routes */}
        <Route path="/rankboard" element={<RankingBoard />} />
        <Route path="/rankboard/companies" element={<RankingBoard />} />
        <Route path="/rankboard/universities" element={<RankingBoard />} />

        {/* Quarantine routes */}
        <Route path="/quarantine" element={<Quarantine />} />
        <Route path="/quarantine/companies" element={<Quarantine />} />
        <Route path="/quarantine/universities" element={<Quarantine />} />
        <Route path="/quarantine/cities" element={<Quarantine />} />

        {/* Issues routes */}
        <Route path="/issues" element={<Issues />} />

        {/* Workflow routes */}
        <Route path="/workflows" element={<Workflows />} />
        {/* Job Description routes */}
        <Route path="/job-descriptions" element={<JobDescriptions />} />
        {/* Assessment Template Routes */}
        <Route path="/sets" element={<AssessmentTemplates />} />
        <Route path="/sets/create" element={<AddEditAssessmentTemplates />} />
        <Route path="/sets/logs" element={<TemplateLogs />} />
        {/* Location Stats routes */}
        {/* <Route path="/location" element={<LocationBoard />} /> */}
        <Route path="/location/actionLogs" element={<ActionsLog />} />
        <Route path="/roles/:roleId/candidates/:candidateId/validateCV" element={<ValidateCV />} />
      {/* // Test routes: Upcoming and Completed Tests */}
      <Route path="/tests" element={<Tests />} />
      <Route path="/tests/:slotId/candidates" element={<SlotCandidates />} />
        <Route
          path="/roles/:roleId/candidates/:candidateId/validateCV"
          element={<ValidateCV />}
        />
      {/* // Test routes: Upcoming and Completed Tests */}
      <Route path="/tests" element={<Tests />} />
      <Route path="/tests/:slotId/candidates" element={<SlotCandidates />} />
{/* for admin */}
        <Route
          path="/onboarding"
          element={
            <AdminDashboard
            />
          }
        />
        <Route
          path="/submit-credentials/:offerId"
          element={<SubmitCredentialsPage />}
        />
        <Route
          path="/roles/:roleId/prompt-config-builder"
          element={<PromptConfigPage />}
        />
      </Routes>
      
      {showFooter && <Footer />}
    </>
  );
}

export default App;
